'use client'
/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react'
import Link from 'next/link'
import ScrollAnimation from '@/components/ScrollAnimation'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import ProductCardMedia from './ProductCardMedia'

const QuickAddButton = dynamic(() => import('./QuickAddButton'), { loading: () => null })

interface ProductCardProps {
  product: any
  displayName: string
  displaySlug: string
  productUrl: string
  productCategory: string
  categoryDisplay: string
  isKgPrice: boolean
  formattedPrice: React.ReactNode
  displayPrice: number
  moqDisplay: string
  isB2CMode: boolean
  vatNumber: string
  vatRate: number
}

export default function ProductCard({
  product,
  displayName,
  productUrl,
  categoryDisplay,
  isKgPrice,
  formattedPrice,
  displayPrice,
  moqDisplay,
  isB2CMode,
}: ProductCardProps) {
  const t = useTranslations('productGrid')
  const [, setSelectedSkuId] = useState<string | null>(null)

  // Listen for color selection events from ProductCardMedia
  useEffect(() => {
    const handleColorSelect = (event: CustomEvent) => {
      if (event.detail.productId === product.id) {
        // Find the SKU ID for the selected color
        const selectedColor = event.detail.selectedColor
        
        // Parse options if it's a string (like in CartSidebar)
        let options = product.options
        if (typeof options === 'string') {
          try {
            options = JSON.parse(options)
          } catch (error) {
            console.error('Error parsing product options:', error)
            options = []
          }
        }
        
        if (Array.isArray(options)) {
          const option = options.find((opt: any) => opt && opt.name === selectedColor)
          setSelectedSkuId(option?.skuId || null)
        } else {
          console.warn('Product options is not an array:', options)
          setSelectedSkuId(null)
        }
      }
    }

    window.addEventListener('productColorSelect', handleColorSelect as EventListener)
    return () => {
      window.removeEventListener('productColorSelect', handleColorSelect as EventListener)
    }
  }, [product.id, product.options])

  return (
    <ScrollAnimation animation="fadeUp" delay={0}>
      <div
        className="group relative rounded-xl md:rounded-2xl overflow-hidden shadow-sm shadow-primary/5 transition-all duration-300"
        role="article"
        aria-label={t('productAriaLabel', { name: displayName })}
      >
        <div className="sr-only">
          <h2>{displayName}</h2>
          {isKgPrice && <p>{t('labels.concentration')}: {product.concentration}</p>}
          <p>{isB2CMode ? t('labels.category') : t('labels.moq')}: {isB2CMode ? categoryDisplay : (isKgPrice ? `${product.moq_grams}g` : `1 ${t('labels.unit')}`)}</p>
          <p>{t('labels.price')}: {displayPrice}</p>
        </div>

        <ProductCardMedia
          productUrl={productUrl}
          productId={product.id}
          defaultImage={product.product_image}
          alt={displayName}
          optionColors={(product as any).option_colors || []}
        />
        
        <div className="relative inset-x-0 bottom-0">
          <div className="p-3 md:p-6 relative overflow-hidden rounded-b-xl backdrop-blur-xl bg-white/8 shadow-2xl shadow-black/20">
            {/* Clean glass surface */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-white/2 rounded-b-xl" />
            <div className="absolute inset-0 bg-gradient-to-tl from-black/3 via-transparent to-white/3 rounded-b-xl" />
            
            
            {/* Content wrapper */}
            <div className="relative z-10">
            <Link href={productUrl} className="block">
              <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-4 text-text-main group-hover:text-primary-light transition-colors duration-300 truncate">
                {displayName}
              </h3>
            </Link>
            <div className="space-y-1 md:space-y-3 relative before:absolute before:left-0 before:top-0 before:w-[2px] before:h-full before:bg-gradient-to-b before:from-primary before:to-secondary before:rounded-full pl-3">
              {isKgPrice && (
                <div className="flex justify-between text-xs md:text-sm">
                  <span className="text-text-secondary font-medium">{t('labels.concentration')}</span>
                  <span className="text-primary-light font-semibold">{product.concentration}</span>
                </div>
              )}
              <div className="flex justify-between text-xs md:text-sm">
                <span className="text-text-secondary font-medium">
                  {isB2CMode ? t('labels.category') : t('labels.moq')}
                </span>
                <span className="text-primary-light font-semibold">
                  {isB2CMode ? categoryDisplay : moqDisplay}
                </span>
              </div>
              <div className="flex justify-between text-xs md:text-sm">
                <span className="text-text-secondary font-medium">{t('labels.price')}</span>
                <span className="text-primary-light font-semibold">{formattedPrice}</span>
              </div>
            </div>
            <div className="my-6">
            </div>
            <div className="flex gap-1 md:gap-4 items-center">
              <Link
                href={productUrl}
                className="flex-1 px-4 md:px-4 py-3 md:py-3 rounded-full bg-text-main text-bg-main text-center hover:bg-secondary hover:scale-103 backdrop-blur-sm transition-all duration-300 font-bold text-sm md:text-base"
                aria-label={t('viewDetails')}
              >
                {t('viewDetails')}
              </Link>
              <QuickAddButton product={product} quantity={isKgPrice ? 1000 : 1} />
            </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollAnimation>
  )
} 